import { computed, inject, Injectable, signal } from '@angular/core';
import { HubConnection, HubConnectionBuilder, HttpTransportType, LogLevel } from '@microsoft/signalr';
import Keycloak from 'keycloak-js';
import { environment } from '../../../environments/environment';
import { StateService } from '../../shared/services/state.service';
import { ErpStatusEvent, ErpStatusService } from './erp-status.service';

// Evento do feed ao vivo — espelha o ActivityEventMessage do backend (camelCase no JSON).
export interface ActivityEvent {
  companyGroupId: number;
  companyId: number;
  type: string;
  title: string;
  detail: string | null;
  link: string | null;
  occurredAt: string;
}

const MAX_EVENTS = 50;

/**
 * Só o que pede AÇÃO vira alerta: falha/recusa do ERP (order-erp-failed, invoice-erp-failed,
 * payment-erp-failed, *-rejected do ack). Recebimento e sucesso são ruído — o painel vivia cheio
 * de "Pedido #… recebido" e a falha se perdia no meio (23/09/2026). Filtrado também no restore,
 * pra limpar o que já estava guardado. Integração desconectada também pede ação (reautorizar).
 */
export function isActionable(type: string): boolean {
  return type.endsWith('-failed') || type.endsWith('-rejected') || type === 'integration-disconnected';
}

/** Retentativa da mesma falha não empilha: o registro (tipo + link) tem UMA linha, a mais nova. */
export function upsertEvent(list: ActivityEvent[], event: ActivityEvent): ActivityEvent[] {
  const same = (e: ActivityEvent) => e.type === event.type && !!event.link && e.link === event.link;
  return [event, ...list.filter((e) => !same(e))].slice(0, MAX_EVENTS);
}

/**
 * Chave do feed persistido, por GRUPO de empresas: o feed recebe os alertas de todas as empresas do
 * grupo que o usuário acessa (ver ActivityHub na API), então trocar de filial não pode esconder o
 * que chegou na outra. Exportada porque `start()` e `clear()` derivam dela.
 */
export function activityStorageKey(companyGroupId: number): string {
  return `mc-activity:g:${companyGroupId}`;
}

/** Chave antiga (por empresa, até 26/09/2026): lida uma vez no restore e apagada. */
export function legacyActivityStorageKey(companyId: number): string {
  return `mc-activity:${companyId}`;
}

/** Junta listas de eventos (mais novo primeiro), sem repetir o mesmo registro (tipo + link). */
export function mergeEvents(...lists: ActivityEvent[][]): ActivityEvent[] {
  const all = lists.flat().sort((a, b) => (b.occurredAt ?? '').localeCompare(a.occurredAt ?? ''));
  return all.reduceRight<ActivityEvent[]>((acc, e) => upsertEvent(acc, e), []);
}

// Feed de atividade ao vivo (SignalR). Conecta uma vez por sessão no hub da API (grupo do tenant é
// resolvido lá pelo companyId + token); eventos chegam por push e alimentam o painel da navbar.
// Best-effort: sem conexão o app segue normal — o feed só fica "aguardando".
@Injectable({ providedIn: 'root' })
export class ActivityService {
  private readonly keycloak = inject(Keycloak);
  private readonly stateService = inject(StateService);
  private readonly erpStatus = inject(ErpStatusService);

  readonly events = signal<ActivityEvent[]>([]);
  readonly unread = signal(0);
  // 'connecting' → 'on' | 'reconnecting' | 'off' (hub indisponível — API antiga/fora; app segue).
  readonly status = signal<'connecting' | 'on' | 'reconnecting' | 'off'>('connecting');
  readonly connected = computed(() => this.status() === 'on');

  private connection?: HubConnection;
  // Persistência por empresa: o feed sobrevive a navegação/reload (antes era efêmero — sumia
  // no primeiro F5, junto com o contador de não-lidos).
  private storageKey?: string;

  // Idempotente — navbar chama ao montar; troca de empresa recarrega a página, que reconecta.
  start(): void {
    if (this.connection) return;
    const companyId = this.stateService.contextAtual;
    const companyGroupId = this.stateService.grupoEmpresaAtual;
    if (!companyId || !companyGroupId) return;

    this.storageKey = activityStorageKey(companyGroupId);
    this.restore(legacyActivityStorageKey(companyId));

    const url = `${environment.serverPath}hubs/activity?companyId=${companyId}`;
    this.connection = new HubConnectionBuilder()
      .withUrl(url, {
        // WebSocket direto (sem negotiate): dispensa CORS/sticky-session; o JWT vai por query
        // (?access_token=), padrão do SignalR pra WebSocket.
        skipNegotiation: true,
        transport: HttpTransportType.WebSockets,
        accessTokenFactory: () => this.keycloak.token ?? '',
      })
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Warning)
      .build();

    this.connection.on('activity', (event: ActivityEvent) => {
      if (!isActionable(event.type)) return;
      const isNew = !this.events().some((e) => e.type === event.type && !!event.link && e.link === event.link);
      this.events.update((list) => upsertEvent(list, event));
      if (isNew) this.unread.update((n) => n + 1);
      this.persist();
    });
    // Status no ERP mudou: não entra no feed (não pede ação), só avisa as telas abertas. A conexão
    // ouve todas as filiais do grupo (ver ActivityHub); as telas mostram só a empresa ativa.
    this.connection.on('erp-status', (event: ErpStatusEvent) => {
      if (Number(event.companyId) === Number(companyId)) this.erpStatus.publish(event);
    });
    this.connection.onreconnected(() => {
      this.status.set('on');
      this.erpStatus.resync(companyId);
    });
    this.connection.onreconnecting(() => this.status.set('reconnecting'));
    this.connection.onclose(() => this.status.set('off'));

    this.connection
      .start()
      .then(() => this.status.set('on'))
      .catch(() => {
        // Sem realtime (hub fora/versão antiga da API): estado OFF honesto — nada de
        // "reconectando" eterno. O app segue normal.
        this.status.set('off');
        this.connection = undefined;
      });
  }

  markAllRead(): void {
    this.unread.set(0);
    this.persist();
  }

  // Saída de limpeza do feed: 50 eventos acumulados só saíam trocando de empresa. Não desliga a
  // conexão — evento novo repovoa a lista normalmente.
  clear(): void {
    this.events.set([]);
    this.unread.set(0);

    const companyGroupId = this.stateService.grupoEmpresaAtual;
    const companyId = this.stateService.contextAtual;
    const key = this.storageKey ?? (companyGroupId ? activityStorageKey(companyGroupId) : null);

    try {
      if (key) localStorage.removeItem(key);
      if (companyId) localStorage.removeItem(legacyActivityStorageKey(companyId));
    } catch {
      // Storage indisponível: a lista em memória já foi zerada, que é o que o usuário vê.
    }
  }

  private persist(): void {
    if (!this.storageKey) return;
    try {
      localStorage.setItem(
        this.storageKey,
        JSON.stringify({ events: this.events(), unread: this.unread() }),
      );
    } catch {
      // Quota/modo privado: feed segue só em memória.
    }
  }

  // Lê o feed do grupo e, uma vez, o da chave antiga por empresa (migração): junta os dois e apaga
  // a antiga. Só o que pede ação entra.
  private restore(legacyKey?: string): void {
    if (!this.storageKey) return;
    const current = this.read(this.storageKey);
    const legacy = legacyKey ? this.read(legacyKey) : null;
    if (!current && !legacy) return;

    const kept = mergeEvents(current?.events ?? [], legacy?.events ?? [])
      .filter((e) => isActionable(e.type))
      .slice(0, MAX_EVENTS);
    this.events.set(kept);
    // Não-lidos guardados contavam o ruído antigo; sem alerta, não há o que ler.
    const unread = (current?.unread ?? 0) + (legacy?.unread ?? 0);
    this.unread.set(Math.min(unread, kept.length));

    if (legacy && legacyKey) {
      try {
        localStorage.removeItem(legacyKey);
      } catch {
        // Sem storage: a migração fica pra próxima.
      }
      this.persist();
    }
  }

  private read(key: string): { events: ActivityEvent[]; unread: number } | null {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return null;
      const saved = JSON.parse(raw) as { events?: ActivityEvent[]; unread?: number };
      return {
        events: Array.isArray(saved.events) ? saved.events : [],
        unread: typeof saved.unread === 'number' && saved.unread >= 0 ? saved.unread : 0,
      };
    } catch {
      // Storage corrompido: começa vazio.
      return null;
    }
  }
}
